import type { CarbonIconType } from "@carbon/icons-react";
import type { TFunction } from "i18next";
import type { Params } from "react-router";
import type { BaseEntity, EntityId } from "src/services/entityServices";
import { ServerError } from "src/services/errors";
import type { UseEntity } from "src/utils/entityHookCreator";
import { extractEntityDisplayName } from "src/utils/entityUtils";

/** A crumb whose text is fixed, such as a section root or a form's verb. */
export type StaticCrumb = {
  label: (t: TFunction) => string;
  /** Section roots carry the icon the main menu uses. */
  icon?: CarbonIconType;
};

/**
 * A crumb naming an entity, read through that entity's own query hook. The hook
 * runs in the breadcrumb, so it must stay query-only — react-query serves it
 * from cache whenever the page below has already asked for the same entity.
 */
export type EntityCrumb = {
  useLabel: (params: Params) => {
    label: string;
    isLoading: boolean;
    /** The entity doesn't exist, so the crumb can say so instead of naming it. */
    notFound?: boolean;
  };
};

export type RouteCrumb = StaticCrumb | EntityCrumb;

/**
 * Route metadata for the breadcrumb trail, carried on `handle` beside `access`
 * (see `getRouteAccess`) and read from `useMatches()`.
 */
export type RouteCrumbHandle = { crumb: RouteCrumb };

const isRouteCrumbHandle = (handle: unknown): handle is RouteCrumbHandle =>
  !!handle && typeof handle === "object" && "crumb" in handle;

export const getRouteCrumb = (handle: unknown): RouteCrumb | undefined =>
  isRouteCrumbHandle(handle) ? handle.crumb : undefined;

export const isEntityCrumb = (crumb: RouteCrumb): crumb is EntityCrumb =>
  "useLabel" in crumb;

/**
 * A crumb mounts after the page below it has asked for the same entity — later
 * still now that crumb hooks load with the route's chunk — and a detail query
 * is stale as soon as it resolves, so without this the crumb would refetch what
 * the page just fetched. Short enough that a stale name cannot outlive the
 * visit that opened the page.
 */
export const CRUMB_STALE_TIME = 1000 * 30;

/**
 * Query keys are type-sensitive, so a page and its crumb must agree on whether
 * the route id is a number or a string; disagreeing opens a second cache entry
 * for the same entity and fetches it twice. Ids that are not numeric — a trait
 * definition's type, say — stay as they are.
 */
export const toEntityId = (id: string | undefined): EntityId | undefined =>
  id && /^\d+$/.test(id) ? Number(id) : id;

/**
 * Names a crumb after the entity in the path, using the section's own query
 * hook and the app-wide display-name rules. Pass a `toLabel` only where the
 * entity is named by something `extractEntityDisplayName` does not know about.
 */
export const createEntityCrumbLabel =
  <T extends BaseEntity>(
    useEntity: UseEntity<T>,
    toLabel: (entity: T) => string | undefined = extractEntityDisplayName,
    /** Segment holding the id, where the route does not call it `id`. */
    idParam = "id",
  ) =>
  (params: Params) => {
    // retryOnMount: false — mounting after the page's own query already
    // failed must not reset that shared query back to pending and restart
    // its retries, which would otherwise loop forever (see EntityQueryOptions).
    const { data, isLoading, error } = useEntity(toEntityId(params[idParam]), {
      staleTime: CRUMB_STALE_TIME,
      retryOnMount: false,
    });

    return {
      label: (data && toLabel(data)) ?? "",
      isLoading,
      notFound: error instanceof ServerError && error.status === 404,
    };
  };
